import type {AgentQuotaSnapshot} from '@github-ui/agent-sessions-types/agent-quota-snapshot'
import type {PreparedPrompt} from '@github-ui/ahp-session-client/contracts'
import {makeFileReference, makeSnippetReference} from '@github-ui/copilot-chat-helpers/copilot-chat-helpers'
import type {ChatInputRef} from '@github-ui/copilot-shared-ui/components/chat-input-ref'
import type {CopilotChatReference, CopilotChatRepo, LineRange} from '@github-ui/copilot-chat-types'
import type {AhpSpaceSelection} from '@github-ui/copilot-shared-ui/contexts/EstablishedAhpComposerContext'
import type {Repository} from '@github-ui/current-repository'
import {isFeatureEnabled} from '@github-ui/feature-flags'

import type {EnvironmentSubmit} from '../../hooks/use-environment-selection'
import type {HandleUserSubmit} from '../../types/user-submit'
import type {AvailableOrganization} from '../../types/task-composer-scope'
import type {McpPermissionMode} from '@github-ui/agent-sessions-types/mcp-permission-mode'

export const MCP_PERMISSION_GATE_FEATURE_FLAG = 'copilot_swe_agent_mcp_permission_gate'

/**
 * Resolves whether the MCP permission gate UI is enabled.
 * The prop is authoritative when provided (true or false); an omitted prop
 * falls back to the legacy `copilot_swe_agent_mcp_permission_gate` feature flag.
 */
export function resolveMcpPermissionGate(mcpPermissionGateEnabled?: boolean): boolean {
  return mcpPermissionGateEnabled ?? isFeatureEnabled(MCP_PERMISSION_GATE_FEATURE_FLAG)
}

export function canPrepareSandboxChat({
  hasChatSubmit,
  showCreateTaskStrip,
  hideRepoSelector,
  showSandboxOption,
  hasSpecifiedRepository,
}: {
  hasChatSubmit: boolean
  showCreateTaskStrip: boolean
  hideRepoSelector: boolean
  showSandboxOption: boolean
  hasSpecifiedRepository: boolean
}): boolean {
  return hasChatSubmit && showCreateTaskStrip && !hideRepoSelector && showSandboxOption && !hasSpecifiedRepository
}

export function getDraftStorageKeySuffix(eventType: string | undefined, repoNameWithOwner: string | undefined): string {
  const base = eventType ?? ''
  return repoNameWithOwner ? `${base}:${repoNameWithOwner}` : base
}

/**
 * Formats snippet/file references as context to append to the problem statement.
 * This ensures the agent session receives the file/line context the user highlighted.
 */
function getReferenceContextLines(references: CopilotChatReference[]): string[] {
  const codeRefs = references.filter(
    (ref): ref is CopilotChatReference & {type: 'snippet' | 'file' | 'file-v2'} =>
      ref.type === 'snippet' || ref.type === 'file' || ref.type === 'file-v2',
  )
  if (codeRefs.length === 0) return []

  return codeRefs.map(ref => {
    let url = ref.url
    if (ref.type === 'snippet' && ref.range) {
      const hash = `#L${ref.range.start}-L${ref.range.end}`
      if (!url.includes('#L')) {
        url = `${url}${hash}`
      }
    }
    return url
  })
}

export function buildPromptWithReferenceContext({
  content,
  references,
  maxPromptLength,
}: {
  content: string
  references: CopilotChatReference[]
  maxPromptLength: number
}): string {
  const contentWithinLimit = content.slice(0, maxPromptLength)
  if (content.length >= maxPromptLength) return contentWithinLimit

  const referenceLines = getReferenceContextLines(references)
  if (referenceLines.length === 0) return contentWithinLimit

  const remainingChars = maxPromptLength - contentWithinLimit.length
  const contextHeader = '\n\nReferenced code:\n'
  if (remainingChars <= contextHeader.length) return contentWithinLimit

  let referenceContext = contextHeader
  for (const line of referenceLines) {
    const separator = referenceContext === contextHeader ? '' : '\n'
    const nextContext = `${referenceContext}${separator}${line}`
    if (nextContext.length > remainingChars) break
    referenceContext = nextContext
  }

  if (referenceContext === contextHeader) return contentWithinLimit
  return `${contentWithinLimit}${referenceContext}`
}

export type CreateTaskReference = {
  path: string
  range?: LineRange
}

export function makeFileOrSnippetReference({
  referenceRepo,
  reference,
}: {
  referenceRepo: CopilotChatRepo
  reference: CreateTaskReference
}) {
  if (reference.range) {
    return makeSnippetReference(reference.path, reference.range, referenceRepo)
  } else {
    return makeFileReference(reference.path, referenceRepo)
  }
}

export type TaskComposerProps = {
  handleUserSubmit: HandleUserSubmit
  initialRepo?: Repository | null
  initialReference?: CreateTaskReference | null
  hideRepoSelector?: boolean
  textAreaRef?: React.RefObject<HTMLTextAreaElement | null>
  chatInputRef?: React.RefObject<ChatInputRef | null>
  className?: string
  eventType?: string
  shouldClearTextOnSubmit?: boolean
  /** Locks the composer while a cloud task submits. AHP submits always lock. */
  disableWhileSubmitting?: boolean
  initialText?: string
  initialBranch?: string
  isNewConversation?: boolean
  maxTextareaHeight?: number
  submitWithModifier?: boolean
  /**
   * Called when the user submits with an AHP environment selected; when omitted,
   * submit falls back to the default cloud path. `repositoryNwo`/`baseRef` carry
   * the selected repo so the task can be provisioned as a worktree.
   */
  onEnvironmentSubmit?: EnvironmentSubmit
  /** Enables repo-less Sandbox Chat and starts it with the prepared prompt. */
  onChatSubmit?: (prompt: PreparedPrompt, metadata?: ChatSubmissionMetadata) => void | Promise<void>
  /** Identifies one application of the preset that selects Sandbox Chat mode. */
  newChatPresetId?: string
  /**
   * Offers the managed-sandbox picker option (requires `onEnvironmentSubmit`).
   * Also gated by the managed-sandbox and AHP environments flags.
   */
  supportsManagedSandbox?: boolean
  agentQuotaSnapshot?: AgentQuotaSnapshot
  /**
   * Authoritative override for the MCP permission-mode selector when provided:
   * `true` forces it visible, `false` forces it hidden. When omitted, visibility
   * falls back to the `copilot_swe_agent_mcp_permission_gate` feature flag.
   */
  mcpPermissionGateEnabled?: boolean
  /**
   * Authoritative override for the create-task strip when provided. When omitted,
   * visibility falls back to the `coding_agent_create_task_strip` feature flag.
   */
  createTaskStripEnabled?: boolean
  /** Login of the current user, used to pre-fill the owner on the new repository URL. */
  currentUserLogin?: string
  /** Avatar URL of the current user, used as the personal-scope avatar in the scope picker. */
  currentUserAvatarUrl?: string
  /** Organizations the user can scope the composer to. Empty/omitted hides the scope picker. */
  availableOrganizations?: AvailableOrganization[]
}

export type ChatSubmissionMetadata = Readonly<Record<string, unknown>> & {
  space?: AhpSpaceSelection
  mcpPermissionMode?: McpPermissionMode
}
